/**
 * App Layout - Persistent Layout Component (Class-based)
 *
 * Converted to a class component to work around a Safari 18.3 bug where
 * ReactSharedInternals.H (hooks dispatcher) is null during this component's
 * render phase. Class components use a completely different rendering path
 * that doesn't depend on the hooks dispatcher.
 *
 * Page props are provided via pageStore (populated from app.tsx) instead
 * of usePage() hook.
 */

import { Component } from 'react'
import type { CSSProperties, ReactNode } from 'react'
import { toast } from 'sonner'
import { Toaster } from '@/components/ui/sonner'
import { SidebarProvider } from '@/components/ui/sidebar'
import { AppSidebar } from '@/components/app-sidebar'
import { getSidebarState } from '@/lib/sidebar-state'
import { ThemeProvider } from '@/components/theme-provider'
import { TwilioProvider } from '@/contexts/TwilioContext'
import { GlobalCallNotification } from '@/components/global-call-notification'
import { pageStore } from '@/lib/page-store'

interface FlashMessages {
  success?: string
  error?: string
  notice?: string
  alert?: string
}

interface AppLayoutProps {
  children: ReactNode
}

interface AppLayoutState {
  pageProps: Record<string, any>
}

const SUCCESS_TOAST_STYLE = {
  '--normal-bg':
    'color-mix(in oklab, light-dark(var(--color-green-600), var(--color-green-400)) 10%, var(--background))',
  '--normal-text': 'light-dark(var(--color-green-600), var(--color-green-400))',
  '--normal-border': 'light-dark(var(--color-green-600), var(--color-green-400))',
} as CSSProperties

const ERROR_TOAST_STYLE = {
  '--normal-bg':
    'color-mix(in oklab, light-dark(var(--color-red-600), var(--color-red-400)) 10%, var(--background))',
  '--normal-text': 'light-dark(var(--color-red-600), var(--color-red-400))',
  '--normal-border': 'light-dark(var(--color-red-600), var(--color-red-400))',
} as CSSProperties

function showFlashToasts(flash: FlashMessages | undefined) {
  if (!flash) return

  if (flash.success) {
    toast.success(flash.success, { style: SUCCESS_TOAST_STYLE })
  }
  if (flash.notice) {
    toast.success(flash.notice, { style: SUCCESS_TOAST_STYLE })
  }
  if (flash.error || flash.alert) {
    toast.error(flash.error || flash.alert, { style: ERROR_TOAST_STYLE })
  }
}

export class AppLayout extends Component<AppLayoutProps, AppLayoutState> {
  private unsubscribeStore: (() => void) | null = null
  private prevFlash: FlashMessages | undefined = undefined
  private overlayFallbackTimeout: ReturnType<typeof setTimeout> | null = null

  state: AppLayoutState = {
    pageProps: pageStore.getProps(),
  }

  componentDidMount() {
    // Subscribe to page store updates (triggered by Inertia navigation)
    this.unsubscribeStore = pageStore.subscribe(() => {
      this.setState({ pageProps: pageStore.getProps() })
    })

    // Show flash toasts for initial page load
    const flash = this.state.pageProps?.flash as FlashMessages | undefined
    showFlashToasts(flash)
    this.prevFlash = flash

    // If showing auth overlay, schedule a fallback re-render in case tokens are
    // cleared by Landing (expired JWT) — pageStore won't update for that case
    if (!this.state.pageProps?.auth?.user && typeof window !== 'undefined' && localStorage.getItem('auth_token')) {
      this.overlayFallbackTimeout = setTimeout(() => this.forceUpdate(), 3000)
    }
  }

  componentDidUpdate(_prevProps: AppLayoutProps, prevState: AppLayoutState) {
    const flash = this.state.pageProps?.flash as FlashMessages | undefined
    const prevFlash = prevState.pageProps?.flash as FlashMessages | undefined

    if (flash !== prevFlash) {
      showFlashToasts(flash)
    }

    // Clear fallback timeout once auth resolves
    if (this.state.pageProps?.auth?.user && this.overlayFallbackTimeout) {
      clearTimeout(this.overlayFallbackTimeout)
      this.overlayFallbackTimeout = null
    }
  }

  componentWillUnmount() {
    this.unsubscribeStore?.()
    if (this.overlayFallbackTimeout) clearTimeout(this.overlayFallbackTimeout)
  }

  render() {
    const { children } = this.props
    // Read latest props directly from pageStore — this.state.pageProps may lag
    // by one render frame during Inertia page transitions, causing a flash of
    // content without sidebar ("zoomed 150%" appearance).
    const pageProps = pageStore.getProps()
    const auth = pageProps?.auth

    // Check if we're in Mini App mode (tab=mini_app in URL)
    const isMiniAppMode = typeof window !== 'undefined' && new URLSearchParams(window.location.search).get('tab') === 'mini_app'

    // If user is not authenticated (login page, etc.), render without sidebar.
    // Children always render so their effects fire (e.g., Landing's auth redirect).
    // A spinner overlay covers the content when a JWT exists in localStorage to
    // prevent the brief "zoomed/no sidebar" flash during the auth redirect cycle.
    if (!auth?.user) {
      const maybeAuthenticating = typeof window !== 'undefined' && !!localStorage.getItem('auth_token')
      return (
        <ThemeProvider>
          <Toaster position="top-right" />
          {maybeAuthenticating && (
            <div className="fixed inset-0 z-50 flex items-center justify-center bg-background">
              <div className="inline-block h-8 w-8 animate-spin rounded-full border-4 border-solid border-primary/20 border-r-primary" />
            </div>
          )}
          {children}
        </ThemeProvider>
      )
    }

    // Mini App mode: render without sidebar/header (mobile viewport only)
    if (isMiniAppMode) {
      return (
        <ThemeProvider>
          <Toaster position="top-right" />
          <div className="flex h-dvh w-full bg-background overflow-hidden">
            <main className="size-full flex-1 min-w-0 overflow-y-auto">
              {children}
            </main>
          </div>
        </ThemeProvider>
      )
    }

    // Determine if user can make/receive calls
    const canUseTwilio = ['super_admin', 'owner', 'admin', 'dispatcher', 'accountant'].includes(auth.user.role)

    // Authenticated pages with sidebar
    return (
      <ThemeProvider>
        <Toaster position="top-right" />
        <TwilioProvider
          enabled={canUseTwilio}
          userId={auth.user.id}
          userName={auth.user.name}
          userRole={auth.user.role}
        >
          <div className="flex h-dvh w-full bg-background overflow-hidden">
            <SidebarProvider
              defaultOpen={getSidebarState()}
            >
              <AppSidebar user={auth.user} />
              <div className="flex flex-1 flex-col min-w-0 bg-background">
                <header className="border-b px-4 py-3 flex items-center gap-4 shrink-0">
                </header>
                <main className="size-full flex-1 px-4 py-6 sm:px-6 min-w-0 overflow-y-auto">
                  {children}
                </main>
              </div>
            </SidebarProvider>
          </div>
          <GlobalCallNotification />
        </TwilioProvider>
      </ThemeProvider>
    )
  }
}
